import { useState } from "react";
import { query } from "../api/client";
import { useQuery } from "../api/useQuery";
import { fmtDateTime, roleName } from "../components/format";
import type { AuditEntry, Page, User } from "../api/types";

const PAGE_SIZE = 50;

// Разделы журнала — префиксы действий.
const GROUPS: [string, string, string[]][] = [
  ["all", "Все", []],
  ["auth", "Входы и отказы", ["auth", "access"]],
  ["users", "Сотрудники и жетоны", ["user", "token"]],
  ["geo", "Территории и точки", ["territory", "route", "tag"]],
  ["reference", "Справочники и настройки", ["reference", "settings"]],
  ["devices", "Устройства", ["device"]],
  ["records", "Исправления, имущество, файлы", ["equipment", "equipment_item", "file", "patrol"]],
  ["export", "Выгрузки", ["export"]],
  ["backup", "Резервные копии", ["backup"]],
];

const ACTIONS: Record<string, string> = {
  "auth.login": "Вход",
  "auth.login_failed": "Неудачная попытка входа",
  "access.denied": "Отказ в доступе",
  "user.create": "Заведён сотрудник",
  "user.update": "Изменены данные сотрудника",
  "user.block": "Сотрудник заблокирован",
  "user.unblock": "Сотрудник разблокирован",
  "user.password_reset": "Сброшен пароль",
  "token.create": "Выдан личный жетон",
  "token.update": "Изменён личный жетон",
  "territory.create": "Заведена территория",
  "territory.update": "Изменена территория",
  "route.create": "Заведён маршрут",
  "route.update": "Изменён маршрут",
  "tag.create": "Заведена контрольная точка",
  "tag.update": "Изменена контрольная точка",
  "reference.create": "Новая запись справочника",
  "reference.update": "Изменена запись справочника",
  "settings.update": "Изменены настройки приложения",
  "device.block": "Блокировка устройства",
  "device.clock_skew": "Расхождение часов устройства",
  "equipment.correct": "Исправление чек-листа смены",
  "patrol.annul": "Обход аннулирован",
  "equipment_item.create": "Заведено имущество",
  "backup.verify": "Проверено восстановление резервной копии",
  "export.full": "Полная выгрузка данных",
  "equipment_item.update": "Изменено имущество",
  "file.download": "Скачано фото охраняемого вида",
  "export.shifts": "Выгрузка: журнал смен",
  "export.checkpoints": "Выгрузка: прохождение точек",
  "export.incidents": "Выгрузка: реестр инцидентов",
  "export.observations": "Выгрузка: реестр наблюдений",
  "export.equipment": "Выгрузка: имущество смен",
  "export.devices": "Выгрузка: устройства",
  "export.track": "Выгрузка: трек обхода",
  "export.tracks": "Массовая выгрузка треков",
  "export.patrol_report": "Отчёт об обходе (PDF)",
  "export.shift_report": "Отчёт о смене (PDF)",
  "user.bulk_export_grant": "Выдано право массовой выгрузки",
  "user.bulk_export_revoke": "Отозвано право массовой выгрузки",
};

// Подписи полей в подробностях; служебные поля без подписи не показываются.
const FIELDS: Record<string, string> = {
  login: "логин", full_name: "ФИО", role: "роль", territory_id: "территория",
  name: "название", code: "код", is_active: "действует", point_name: "точка",
  uid_hex: "UID", status: "статус", radius_m: "радиус, м", route_id: "маршрут",
  lat: "широта", lon: "долгота", sort_order: "порядок", latin_name: "латынь",
  is_protected: "охраняемый", track_interval_s: "интервал трека, с",
  track_distance_m: "смещение, м", sync_interval_min: "синхронизация, мин",
  blocked: "заблокировано", reason: "причина", inventory_no: "номер",
  condition: "состояние", note: "замечание", species: "вид", skew_seconds: "расхождение, с",
  format: "формат", rows: "строк", filter: "условия", patrol_id: "обход", shift_id: "смена",
  kind: "вид", assigned_to: "закреплено", timezone: "часовой пояс",
  lock_after_min: "блокировка, мин", offline_access_days: "офлайн-вход, дн", serial_no: "маркировка", user_id: "сотрудник", restored: "восстановлено", tables: "таблиц", files: "файлов", method: "запрос",
};

/** Журнал аудита: кто, что и когда менял в системе. Только просмотр. */
export function AuditPage() {
  const [group, setGroup] = useState("all");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);
  const users = useQuery<{ items: User[] }>("/api/v1/users");

  const path = "/api/v1/audit" + query({
    groups: GROUPS.find(([id]) => id === group)?.[2].join(","),
    actor_id: actor,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(new Date(to).getTime() + 86400000).toISOString() : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const { data, error, loading } = useQuery<Page<AuditEntry>>(path);
  const reset = (fn: () => void) => { fn(); setOffset(0); };

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Журнал аудита</h2>
          <p className="muted small">Записи нельзя изменить или удалить</p>
        </div>
      </div>

      <div className="filters">
        <div className="segmented" role="group" aria-label="Раздел">
          {GROUPS.map(([id, title]) => (
            <button key={id} aria-pressed={group === id} onClick={() => reset(() => setGroup(id))}>{title}</button>
          ))}
        </div>
      </div>
      <div className="filters">
        <label className="field">Сотрудник
          <select value={actor} onChange={(e) => reset(() => setActor(e.target.value))}>
            <option value="">Все</option>
            {users.data?.items.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </label>
        <label className="field">С даты
          <input type="date" value={from} onChange={(e) => reset(() => setFrom(e.target.value))} />
        </label>
        <label className="field">По дату
          <input type="date" value={to} onChange={(e) => reset(() => setTo(e.target.value))} />
        </label>
        {(actor || from || to) && (
          <button className="secondary" onClick={() => reset(() => { setActor(""); setFrom(""); setTo(""); })}>Сбросить</button>
        )}
      </div>

      {error && <div className="error" role="alert">{error}</div>}
      {loading && !data && <p className="notice">Загрузка…</p>}
      {data && data.items.length === 0 && <p className="notice">Записей нет.</p>}

      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Когда</th><th>Кто</th><th>Действие</th><th>Объект</th><th>Подробности</th><th>IP</th></tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr key={`${e.at}-${i}`}>
                    <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(e.at)}</td>
                    <td>{e.actor_name ?? <span className="muted">система</span>}</td>
                    <td>
                      <span className={e.action === "auth.login_failed" || e.action === "access.denied" ? "badge danger" : undefined}>
                        {ACTIONS[e.action] ?? e.action}
                      </span>
                    </td>
                    <td>{objectText(e)}</td>
                    <td className="small"><Detail detail={e.detail} /></td>
                    <td className="small muted">{e.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.total > PAGE_SIZE && (
        <div className="filters" style={{ marginTop: 14 }}>
          <button className="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Назад</button>
          <span className="muted small">{offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} из {data.total}</span>
          <button className="secondary" disabled={offset + PAGE_SIZE >= data.total} onClick={() => setOffset(offset + PAGE_SIZE)}>Далее</button>
        </div>
      )}
    </>
  );
}

function objectText(e: AuditEntry): string {
  if (e.action.startsWith("auth.")) return "";
  if (e.object_label) return e.object_label;
  if (e.object_kind === "reference" || e.object_kind === "settings" || e.object_kind === "request") return e.object_id;
  return e.object_id ? `${e.object_id.slice(0, 8)}…` : "";
}

function fmtValue(key: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "да" : "нет";
  if (key === "role" && typeof v === "string") return roleName[v] ?? v;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** Подробности: у правок — только изменившиеся поля «было → стало». */
function Detail({ detail }: { detail: Record<string, unknown> | null }) {
  if (!detail) return null;
  const old = detail.old as Record<string, unknown> | undefined;
  const now = detail.new as Record<string, unknown> | undefined;
  if (old && now) {
    const changed = Object.keys(now).filter((k) => FIELDS[k] && JSON.stringify(old[k]) !== JSON.stringify(now[k]));
    if (changed.length === 0) return <span className="muted">без изменений</span>;
    return (
      <>
        {changed.map((k) => (
          <div key={k}>{FIELDS[k]}: <s className="muted">{fmtValue(k, old[k])}</s> → {fmtValue(k, now[k])}</div>
        ))}
      </>
    );
  }
  // Исправление чек-листа: причина и исправленные поля.
  const flat = { ...detail, ...((detail.patch as Record<string, unknown>) ?? {}) };
  const keys = Object.keys(flat).filter((k) => FIELDS[k]);
  return <>{keys.map((k) => <div key={k}>{FIELDS[k]}: {fmtValue(k, flat[k])}</div>)}</>;
}
